import React, { useState } from "react";
import styles from "./CreateQuizTemplate.module.css";
import QuizIcon from "@mui/icons-material/Quiz";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import AddToPhotosIcon from "@mui/icons-material/AddToPhotos";
import SubjectIcon from "@mui/icons-material/Subject";
import CloseIcon from "@mui/icons-material/Close";
import {
  useHistory,
  useLocation,
  useParams,
} from "react-router-dom/cjs/react-router-dom.min";
import { handleErrorMessage } from "../../../../../redux/actions/classes";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { setTitleAndInstruction } from "../../../../../redux/actions/courses";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  Paper,
  Typography,
  Box
} from "@mui/material";
import { IconButton } from "@mui/material";

const CreateQuizTemplate = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();

  const token = useSelector((state) => state.auth?.token); // adjust to your auth state
  const token1 = localStorage.getItem("token"); // redux isn't working for some reason, using localStorage as a fallback

  const [title, setTitle] = useState("");
  const [instructions, setInstructions] = useState("");

  // Modal states
  const [choiceOpen, setChoiceOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);

  // AI form states
  const [topic, setTopic] = useState("");
  const [numQuestions, setNumQuestions] = useState(5);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState([]);

  const toggleType = (value) => {
    setTypes((prev) =>
      prev.includes(value) ? prev.filter((t) => t !== value) : [...prev, value]
    );
  };

  const toggleAll = () => {
    const allValues = [
      "mcq",
      "checkboxes",
      "grid",
      "fillBlanks",
      "trueFalse",
      "imageChoice",
    ];
    setTypes((prev) => (prev.length === allValues.length ? [] : allValues));
  };

  const handleNavigation = () => {
    if (title.trim() !== "") {
      dispatch(setTitleAndInstruction({ title, instruction: instructions }));
      setChoiceOpen(true);
    } else {
      dispatch(handleErrorMessage("Please enter required fields!"));
    }
  };

  const handleManual = () => {
    history.push(`/template/questions/${id}`, {
      title,
      instructions,
      sectionId: location?.state?.sectionId,
    });
  };

  const handleContinueFromChoice = () => {
    if (selectedOption === "manual") {
      setChoiceOpen(false);
      setTimeout(() => handleManual(), 150);
    } else if (selectedOption === "ai") {
      setChoiceOpen(false);
      setTimeout(() => setAiOpen(true), 150);
    }
  };

  const handleGenerateAI = async () => {
    if (!topic.trim()) {
      dispatch(handleErrorMessage("Please enter a quiz topic."));
      return;
    }
    if (!numQuestions || Number(numQuestions) <= 0) {
      dispatch(handleErrorMessage("Please enter a valid number of questions."));
      return;
    }
    if (Number(numQuestions) > 50) {
      dispatch(handleErrorMessage("Maximum 50 questions are allowed."));
      return;
    }

    const selectedTypes = types.length ? types : ["mcq"];
    
    setLoading(true);
    setGenerated([]);
    try {
      const payload = {
      prompt_id: 1,
        variables: {
          topic,
          number_of_questions: Number(numQuestions),
          type_of_questions: selectedTypes.join(","),
        },
      };

      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}api/v1/quiz/quiz-list-ai/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `token ${token1}`,
          },
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) throw new Error("Failed to generate questions");
      const data = await res.json();
      history.push(`/template/questions/${id}`, {
        title,
        instructions,
        sectionId: location?.state?.sectionId,
        questions: data?.data || [],
      });
    } catch (err) {
      console.error(err);
      dispatch(handleErrorMessage("Error generating questions"));
    } finally {
      setLoading(false);
    }
  };

  const handleUseGenerated = () => {
    // Navigate to questions page with generated questions in state
    history.push(`/template/questions/${id}`, {
      title,
      instructions,
      sectionId: location?.state?.sectionId,
      questions: generated,
    });
  };

  const handleGoBack = () => {
    history.push(`/course/content/${id}`);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button className={styles.backButton}>
          <ArrowLeftIcon size={20} />
          <span onClick={handleGoBack}>
            {t("courseMonetisation.goBackBtn")}
          </span>
        </button>
        <div className={styles.titleContainer}>
          <div className={styles.iconContainer}>
            <QuizIcon sx={{ color: "#fff" }} />
          </div>
          <h1 className={styles.mainTitle}>{t("quiz.quizTemplate")}</h1>
        </div>
        <button onClick={handleNavigation} className={styles.nextButton}>
          {t("createCourse.next")}
        </button>
      </div>

      <div className={styles.content}>
        <div className={styles.addTemplateHeader}>
          <AddToPhotosIcon sx={{ color: "#F33070" }} />
          <span className={styles.addTemplateText}>
            {t("quiz.addQuizTemplate")}
          </span>
        </div>

        <div className={styles.formContainer}>
          <div className={styles.formField}>
            <label className={styles.fieldLabel}>
              <span className={styles.fieldIcon}>
                <SubjectIcon sx={{ color: "#F33070" }} />
              </span>
              <span className={styles.fieldLable}>
                {t("quiz.quizTemplateTitle")}
                <span className={styles.required}>*</span>
              </span>
            </label>
            <input
              type="text"
              placeholder={t("quiz.addTemplateTitle")}
              className={styles.inputField}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className={styles.formField}>
            <label className={styles.fieldLabel}>
              <span className={styles.fieldIcon}>
                <SubjectIcon sx={{ color: "#F33070" }} />
              </span>
              <span className={styles.fieldLable}>{t("quiz.instruction")}</span>
            </label>
            <textarea
              placeholder={t("quiz.shareInstructionPlaceholder")}
              className={styles.textareaField}
              rows={5}
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Choice Modal */}
      <Dialog
        open={choiceOpen}
        onClose={() => setChoiceOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: { borderRadius: "30px", p: 3, position: "relative", width: 680, maxWidth: "95%", }
        }}
      >
        <img
          src={"/Image/quiz/Quiz-1.png"}
          alt=""
          style={{ position: "absolute", top: 12, left: 12, width: 160, height: 80 }}
        />

        {/* Header */}
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: "600",
            fontSize: "28px",
            mt: 6,
            pb: 0
          }}
        >
          How do you want to create quiz template?
        </DialogTitle>

        <IconButton
          aria-label="close"
          onClick={() => setChoiceOpen(false)}
          sx={{ position: "absolute", top: 12, right: 12 }}
        >
          <CloseIcon />
        </IconButton>

        {/* Options (cards are toggles now) */}
        <DialogContent
          sx={{
            display: "flex",
            gap: 4,
            mt: 0,
            flexDirection: { xs: "column", sm: "row" }
          }}
        >
          {/* Manual Creation Card (toggle selection) */}
          <Paper
            onClick={() => setSelectedOption((prev) => (prev === "manual" ? null : "manual"))}
            sx={{
              flex: 1,
              p: 3,
              textAlign: "center",
              borderRadius: 3,
              cursor: "pointer",
              border: "1px solid #eee",
              transition: "all 0.25s ease",
              boxShadow: selectedOption === "manual" ? "0px 0px 10px 3px #F4ADC7" : "0px 0px 10px 3px #00000040",
            }}
          >
            <img src={"/Image/quiz/Quiz-manual.png"} alt="manual" style={{ width: 110, height: 110, marginBottom: 4 }} />
            <Typography fontSize="18px" fontWeight="600">Manual Creation</Typography>
            <Typography fontSize="14px" fontWeight="400" color="#000" sx={{ mt: 1 }}>
              Add questions one by one, set options and customize the quiz as per your need.
            </Typography>
          </Paper>

          {/* AI Powered Creation Card (toggle selection) */}
          <Paper
            onClick={() => setSelectedOption((prev) => (prev === "ai" ? null : "ai"))}
            sx={{
              flex: 1,
              p: 3,
              textAlign: "center",
              borderRadius: 3,
              cursor: "pointer",
              border: "1px solid #eee",
              transition: "all 0.25s ease",
              boxShadow: selectedOption === "ai" ? "0px 0px 10px 3px #F4ADC7" : "0px 0px 10px 3px #00000040",
            }}
          >
            <img src={"/Image/quiz/Quiz-AI.png"} alt="ai" style={{ width: 92, height: 92, marginBottom: 8, marginTop: 13 }} />
            <Typography fontSize="18px" fontWeight="600">AI-Powered Creation</Typography>
            <Typography fontSize="14px" fontWeight="400" color="#000" sx={{ mt: 1 }}>
              Let AI instantly generate a full quiz based on your chosen topic or content.
            </Typography>
          </Paper>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "end", pt: 2 }}>
          <Button
            variant="contained"
            onClick={handleContinueFromChoice}
            disabled={!selectedOption}
            sx={{
              borderRadius: "60px",
              fontFamily: "Montserrat,Arial,sans-serif",
              background: "#F33070",
              color: "#fff",
              fontSize: "14px",
              fontWeight: 700,
              paddingX: 3,
              textTransform: "none",
              width: "140px",
              height: "45px",
              opacity: selectedOption ? 1 : 0.6,
              mr: 2,
            }}
          >
            CONTINUE
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI Modal */}
      <Dialog
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 4,
            p: 0,
            overflow: "hidden",
          },
        }}
      >
        <Box sx={{ p: 4, bgcolor: "#fff" }}>
          {/* Header */}
          <Box sx={{ textAlign: "center", mb: 2 }}>
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ mb: 0.5 }}
            >
              Create Quiz with nxNearby AI
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Let AI help you generate quizzes in seconds
            </Typography>
          </Box>

          {/* Topic Input */}
          <Typography fontWeight="bold" sx={{ mt: 3, mb: 1 }}>
            Quiz Topic <span style={{ color: "red" }}>*</span>
          </Typography>
          <TextField
            placeholder="e.g., Python, Machine Learning, Data Structures..."
            fullWidth
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            size="small"
            required
          />

          {/* Number of Questions */}
          <Typography fontWeight="bold" sx={{ mt: 3, mb: 1 }}>
            Number of Questions <span style={{ color: "red" }}>*</span>
          </Typography>
          <TextField
            placeholder="e.g., 5, 10, 20"
            type="number"
            fullWidth
            value={numQuestions}
            onChange={(e) => setNumQuestions(e.target.value)}
            size="small"
            required
          />

          {/* Preferences */}
          <Typography fontWeight="bold" sx={{ mt: 3, mb: 1 }}>
            Quiz Preferences (Optional)
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: 1,
            }}
          >
            {[
              { value: "mcq", label: "Multiple Choice" },
              { value: "checkboxes", label: "Checkbox" },
              { value: "grid", label: "Grid" },
              { value: "fillBlanks", label: "Fill in the Blank" },
              { value: "trueFalse", label: "True/False" },
              { value: "imageChoice", label: "Image Choice" },
            ].map((opt) => (
              <Button
                key={opt.value}
                variant={types.includes(opt.value) ? "contained" : "outlined"}
                size="small"
                onClick={() => toggleType(opt.value)}
                sx={{
                  justifyContent: "flex-start",
                  borderRadius: 2,
                  textTransform: "none",
                }}
              >
                {opt.label}
              </Button>
            ))}
          </Box>

          {/* Select All */}
          <Typography
            sx={{
              mt: 1,
              color: "#F33070",
              fontSize: "0.85rem",
              cursor: "pointer",
            }}
            onClick={toggleAll}
          >
            {types.length === 5 ? "Deselect All" : "Select All"}
          </Typography>

          {/* Loading */}
          {loading && (
            <Box textAlign="center" my={2}>
              <CircularProgress />
            </Box>
          )}

          {/* Generated Questions */}
          {generated.length > 0 && (
            <Box mt={2}>
              <Typography variant="subtitle1">Generated Questions:</Typography>
              {generated.map((q, idx) => (
                <Paper
                  key={idx}
                  sx={{ p: 1, my: 1, bgcolor: "#f9f9f9" }}
                  variant="outlined"
                >
                  {q}
                </Paper>
              ))}
            </Box>
          )}

          {/* Footer Button */}
          <Button
            fullWidth
            variant="contained"
            sx={{
              mt: 3,
              bgcolor: "#F33070",
              "&:hover": { bgcolor: "#d81b60" },
              py: 1.2,
              fontWeight: "bold",
              textTransform: "none",
              fontSize: "1rem",
            }}
            onClick={handleGenerateAI}
            disabled={loading}
          >
            Generate With AI ⚡
          </Button>

          {generated.length > 0 && (
            <Button
              fullWidth
              variant="outlined"
              sx={{ mt: 2, py: 1 }}
              onClick={handleUseGenerated}
            >
              Use Generated
            </Button>
          )}
        </Box>
      </Dialog>
    </div>
  );
};

export default CreateQuizTemplate;
